import classNames from "classnames";
import ControlFilter from "components/shared/ControlFilter";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import { useDateSelectorShortcuts } from "components/shared/DateRangeSelectors/utils";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import { SmallBodyHighlight, TinyBody } from "helpers/TypographyComponents";
import { isObjectEmpty } from "helpers/utils";
import { useCalculationBaseColumnLabels } from "hooks/useCalculationBaseColumnLabels";
import useMounted from "hooks/useMounted";
import { useEffect, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { DashboardData, TopPerformingData } from "store/analyticsStore";
import { fetchDashboardData } from "store/analyticsStore";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { IDatesRange } from "types";

import { useIsAnalyticsRefreshing } from "../../RefreshProgress/context";
import LoadingBlurFilter from "../LoadingBlurFilter";
import type { StoredPreferences } from "../utils";
import { ANALYTICS_DATE_PICKER_MAX_END, hydrateDates, settingsTransform } from "../utils";
import styles from "./style.module.scss";
import { TopBeneficiarList, TopTitleList } from "./topDataList";
import { getTopBeneficiarData, getTopTitleData } from "./utils";

export type TopDataType = "titles" | "authors";
/**
 * Analytics component, shows top data for titles or authors
 */
interface ITopPerformingDataBlock {
    /**
     * Data for performing titles / authors
     */
    dashboard: DashboardData | undefined;
    /**
     * Stored preferences for filters
     */
    storedPreferences: StoredPreferences;
    /**
     * Data type
     */
    type: "top-performing-data";
    /**
     * Date range for periods
     */
    periodsDateRange: IDatesRange;
    /**
     * Fetch data status
     */
    fetchStatus: TFetchStatus;
}

const TopPerformingDataBlock = ({
    dashboard,
    storedPreferences,
    type,
    periodsDateRange,
    fetchStatus,
}: ITopPerformingDataBlock) => {
    const mounted = useMounted(1000);
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { resolveLabel } = useCalculationBaseColumnLabels();
    const { chosenDates: storedDates, dataType: storedDataType } = storedPreferences?.storedFilters?.[type] || {};
    const isRefreshing = useIsAnalyticsRefreshing();
    const isLoading = isRefreshing || (fetchStatus !== "succeeded" && !dashboard);

    const { refreshAnalyticsStatus } = useAppSelector((state) => state.analytics);
    const { periodsShortcuts } = useDateSelectorShortcuts();

    const [chosenDates, setChosenDates] = useState<IDatesRange>(hydrateDates(storedDates) || periodsDateRange);

    useEffect(() => {
        if (periodsDateRange?.startDate && isObjectEmpty(chosenDates)) {
            setChosenDates(periodsDateRange);
        }
    }, [periodsDateRange]);

    const [dataType, setDataType] = useState<TopDataType>((storedDataType as TopDataType) || "titles");

    const translate = {
        titles: { label: `Top 5 - ${t("Analytics.Titles")}` },
        authors: { label: `Top 5 - ${t("Analytics.Beneficiaries")}` },
    };

    const topTitleData = getTopTitleData(dashboard) as TopPerformingData[];

    const topBeneficiarData = getTopBeneficiarData(dashboard) as TopPerformingData[];

    /**
     * Trigger fetch when chosen fetch params options
     */
    useEffect(() => {
        if (isRefreshing || isObjectEmpty(chosenDates) || !chosenDates) return;

        dispatch(fetchDashboardData({ dates: chosenDates, type }));
    }, [chosenDates]);

    /**
     * Save all options/filters in company settings
     */
    useEffect(() => {
        if (!mounted) return;

        const preferences = { chosenDates, dataType };
        const settingsValue = settingsTransform("filters", preferences, storedPreferences, type);
        dispatch(putCompanySettings([{ settingsName: "analytics_layout", settingsValue }]));
    }, [chosenDates, dataType]);

    const dataTypeOptions = [
        { value: "titles", label: t("Analytics.Titles") },
        { value: "authors", label: t("Analytics.Beneficiaries") },
    ];

    return (
        <>
            {/** GRAPH LOADING FILTER */}
            {isLoading && <LoadingBlurFilter />}

            <div
                className={classNames({ [styles["top-performing-data-header"]]: true, [styles["loading"]]: isLoading })}
            >
                <SmallBodyHighlight>{translate[dataType].label}</SmallBodyHighlight>
                <div className={styles["date-range-filters"]}>
                    {!isObjectEmpty(chosenDates) && (
                        <DateRangeSelectors
                            dateRange={periodsDateRange}
                            maxEndDate={ANALYTICS_DATE_PICKER_MAX_END}
                            defaultValues={chosenDates}
                            rangeShortcuts={periodsShortcuts}
                            handleChanges={(newDatesRange) => setChosenDates(newDatesRange as IDatesRange)}
                        />
                    )}
                </div>
            </div>
            <ScrollShadowWrapper className={styles["top-data-list"]}>
                {dataType === "titles" && <TopTitleList topTitleData={topTitleData} isLoading={isLoading} />}
                {dataType === "authors" && (
                    <TopBeneficiarList topBeneficiarData={topBeneficiarData} isLoading={isLoading} />
                )}
            </ScrollShadowWrapper>
            <div className={styles["top-performing-data-footer"]}>
                <TinyBody color="grey">Base : {resolveLabel("turnover")}</TinyBody>
                <ControlFilter
                    controlValue={dataType}
                    filterOptions={dataTypeOptions}
                    onValueChange={(value) => setDataType(value)}
                />
            </div>
        </>
    );
};

export default React.memo(TopPerformingDataBlock);
